import React, { useCallback, useMemo, useState } from 'react';

import { Menu, Paper } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import {
  gridFilterModelSelector,
  gridRowSelectionIdsSelector,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import {
  CircleDot,
  Copy,
  CornerDownRight,
  CornerUpRight,
  Download,
  FileType,
  Minus,
  MoreVertical,
  Share2,
  Sparkles,
  SquarePen,
  Star,
  Tags,
  Trash2,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ReportRow } from 'src/components';
import {
  AlineButton,
  AlineTooltip,
  BulkSetDocumentLabels,
  ConfirmationDialog,
  ConfirmationDialogVariant,
  createUpdateAllDocsMsg,
  LoadingSnackbar,
  LockConfirmationModal,
  NameDialog,
  NameDialogVariant,
  OptionsMenuItem,
  ShareDocumentDialog,
} from 'src/components';
import { $chatCallbacks, $chatThreadState } from 'src/components/ai/chat/chat-state/chat-state';
import { CancelCircleIcon } from 'src/components/icons/CancelCircleIcon';
import { CheckCircleIcon } from 'src/components/icons/CheckCircleIcon';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import {
  collectRowQuestionJobs,
  getJobsLockState,
  getLockAction,
  LockIcon,
  ROWS_LOCK_LABEL,
} from 'src/components/reports/report-detail/lock-helpers';
import type { LockAction } from 'src/components/reports/report-detail/lock-helpers';
import { getIsReportGridRow } from 'src/components/reports/types';
import {
  buildDocumentMentionsToInsert,
  getAllSelectedDocumentsInChat,
  getHasUnloadedSelectedDocuments,
} from 'src/components/repository/repository-chat-mentions';
import { RunCoreAiDialog } from 'src/components/repository/RunCoreAiDialog';
import { getBackToAIRoute } from 'src/editor/toolbars/right-panel/BackToAIButton';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import {
  getSelectedGroup,
  refreshDataGrid,
  useAreAllRowsSelected,
  useExportDocumentsToPdf,
  useGetDocumentsSimple,
  useNotificationDispatch,
  useSelectedDocumentIds,
  useTrashDocuments,
} from 'src/hooks';
import type { AffectedRelationshipParents, TrashDocuments } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { NotificationsActionType } from 'src/notifications';
import { Sentry } from 'src/sentry';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import { useSnapshot } from 'valtio';

import { BulkExportDialog } from './BulkExportDialog';
import { ChangeContractTypeDialog } from './ChangeContractTypeDialog';
import { DuplicateDocumentDialog } from './DuplicateDocumentDialog';
import { SetDocumentsStatusDialog } from './SetDocumentsStatusDialog';
import { SelectRelatedDocumentsWithRelationshipDialog } from '../generic/SelectRelatedDocumentsWithRelationshipDialog';
import type { RelatedDocumentsDirection } from '../generic/SelectRelatedDocumentsWithRelationshipDialog';

export type TotalDocumentCount = 'no-select-all' | number;

interface RepositoryActionsProps {
  totalDocumentCount: TotalDocumentCount;
  isRelationshipGrouping?: boolean;
  missingMatchesOnly?: boolean;
  onDocumentsTrashed?: (
    documentIds: readonly UUID[],
    affectedRelationshipParents?: AffectedRelationshipParents | null
  ) => void;
  onGroupTrashed?: (groupRowId: string, trashedCount: number) => void;
  reportId?: UUID;
  showRowsLockAction?: boolean;
  updateCacheOnTrash?: TrashDocuments['updateCacheOnTrash'];
}

type RepositoryActionDialogTypes =
  | 'bulk-export'
  | 'contract-type'
  | 'extraction-warning'
  | 'trash'
  | 'duplicate-document'
  | 'rename'
  | 'share'
  | 'set-status'
  | 'bulk-set-labels'
  | 'run-core-ai'
  | null;

const maxFilesToDownload = 100;

const useStyles = makeStyles()(theme => ({
  arrowIcon: {
    marginLeft: theme.spacing(1),
  },
  menu: {
    transform: 'translateY(-12px)',
  },
  moreButton: {
    minWidth: 'unset',
    padding: theme.spacing(0.75, 1),
  },
}));

export function RepositoryActions({
  isRelationshipGrouping = false,
  reportId,
  totalDocumentCount,
}: RepositoryActionsProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState<boolean>(false);
  const { handleRenameDocument } = useRenameDocumentRow();
  const { selectedDocumentIds } = useSelectedDocumentIds(apiRef);
  const { documents: selectedDocuments } = useGetDocumentsSimple(selectedDocumentIds, {
    skip: selectedDocumentIds.length !== 1,
  });
  const [runCoreAiForDocuments] = Apollo.useRunCoreAiForDocumentsMutation();

  // Dialog/menu UI state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openDialog, setOpenDialog] = useState<RepositoryActionDialogTypes>(null);
  const [relationshipDialogDirection, setRelationshipDialogDirection] =
    useState<RelatedDocumentsDirection | null>(null);
  const { shouldRunForAllDocuments } = useAreAllRowsSelected(apiRef, totalDocumentCount);
  const numericTotalDocumentCount = typeof totalDocumentCount === 'number' ? totalDocumentCount : 0;
  const disabledReason = !selectedDocumentIds.length ? 'No documents selected' : '';

  // Relationship actions derived flags
  const firstSelectedDocumentId = selectedDocumentIds.at(0) ?? null;
  const firstSelectedDocumentName = selectedDocuments.at(0)?.name ?? 'Untitled document';

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineButton
            ref={setAnchorEl}
            buttonClassName={classes.moreButton}
            disabled={Boolean(disabledReason)}
            variant="outlined"
            onClick={() => setOpen(!open)}
          >
            <MoreVertical size={18} />
          </AlineButton>
        </div>
      </AlineTooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        className={classes.menu}
        elevation={1}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('share');
          }}
          disabled={selectedDocumentIds.length !== 1}
          icon={<Share2 size={16} />}
          text="Share"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('rename');
          }}
          disabled={selectedDocumentIds.length !== 1}
          icon={<SquarePen size={16} />}
          text="Rename"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('bulk-set-labels');
          }}
          disabled={!selectedDocumentIds.length}
          icon={<Tags size={16} />}
          text="Set labels"
        />

        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('run-core-ai');
          }}
          disabled={!selectedDocumentIds.length}
          icon={<Sparkles size={16} />}
          text="Run core AI"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('duplicate-document');
          }}
          disabled={selectedDocumentIds.length !== 1}
          icon={<Copy size={16} />}
          text="Duplicate document"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('set-status');
          }}
          disabled={!selectedDocumentIds.length}
          icon={<CircleDot size={16} />}
          text="Set document status"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setOpenDialog('contract-type');
          }}
          disabled={!selectedDocumentIds.length}
          icon={<FileType size={16} />}
          text="Change contract type"
        />
        {reportId &&
          !isRelationshipGrouping &&
          typeof totalDocumentCount === 'number' &&
          totalDocumentCount > 0 && (
            <OptionsMenuItem
              clickHandler={() => {
                setOpen(false);
                setOpenDialog('bulk-export');
              }}
              text={`Export all (${totalDocumentCount})`}
            />
          )}
        {selectedDocumentIds.length === 1 && (
          <>
            <OptionsMenuItem
              clickHandler={() => {
                setOpen(false);
                setRelationshipDialogDirection('child');
              }}
              disabled={!reportId}
              icon={<CornerDownRight size={16} />}
              text="Add child relationship"
            />
            <OptionsMenuItem
              clickHandler={() => {
                setOpen(false);
                setRelationshipDialogDirection('parent');
              }}
              disabled={!reportId}
              icon={<CornerUpRight size={16} />}
              text="Add parent relationship"
            />
          </>
        )}
      </Menu>
      {reportId && typeof firstSelectedDocumentId === 'string' && relationshipDialogDirection && (
        <SelectRelatedDocumentsWithRelationshipDialog
          open
          afterRelationshipsSaved={() => {
            apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' });
          }}
          direction={relationshipDialogDirection}
          documentId={firstSelectedDocumentId}
          repositoryId={reportId}
          sourceDocumentName={firstSelectedDocumentName}
          onClose={() => setRelationshipDialogDirection(null)}
        />
      )}
      <NameDialog
        handleClose={() => setOpenDialog(null)}
        initialName={selectedDocuments.at(0)?.name ?? ''}
        open={openDialog === 'rename'}
        variant={NameDialogVariant.RenameDocument}
        onFormikSubmit={async name => {
          if (selectedDocumentIds.length !== 1) return;

          setOpenDialog(null);
          await handleRenameDocument(selectedDocumentIds[0], name);
        }}
      />
      {typeof firstSelectedDocumentId === 'string' && (
        <ShareDocumentDialog
          documentId={firstSelectedDocumentId}
          handleClose={() => setOpenDialog(null)}
          open={openDialog === 'share'}
        />
      )}
      <RunCoreAiDialog
        documentCount={
          shouldRunForAllDocuments && typeof totalDocumentCount === 'number'
            ? totalDocumentCount
            : selectedDocumentIds.length
        }
        open={openDialog === 'run-core-ai'}
        onClose={() => setOpenDialog(null)}
        onConfirm={async () => {
          if (!selectedDocumentIds.length) return;

          await runCoreAiForDocuments({
            variables: {
              input: {
                documentIds: selectedDocumentIds,
                reportId,
                runAllDocs: shouldRunForAllDocuments,
              },
            },
          });
        }}
      />
      <SetDocumentsStatusDialog
        apiRef={apiRef}
        open={openDialog === 'set-status'}
        reportId={reportId}
        selectedDocumentIds={selectedDocumentIds}
        shouldRunForAllDocuments={shouldRunForAllDocuments}
        totalDocumentCount={numericTotalDocumentCount}
        onClose={() => setOpenDialog(null)}
      />
      <ChangeContractTypeDialog
        apiRef={apiRef}
        open={openDialog === 'contract-type'}
        reportId={reportId}
        selectedDocumentIds={selectedDocumentIds}
        shouldRunForAllDocuments={shouldRunForAllDocuments}
        totalDocumentCount={numericTotalDocumentCount}
        onClose={() => setOpenDialog(null)}
      />
      <BulkSetDocumentLabels
        apiRef={apiRef}
        open={openDialog === 'bulk-set-labels'}
        reportId={reportId}
        runAllDocs={shouldRunForAllDocuments}
        selectedDocumentIds={selectedDocumentIds}
        totalDocumentCount={numericTotalDocumentCount}
        onClose={() => setOpenDialog(null)}
      />
      {reportId && (
        <BulkExportDialog
          documentCount={typeof totalDocumentCount === 'number' ? totalDocumentCount : 0}
          // Always a report-wide export from this menu entry, so plumb the
          // live grid filterModel through. Without it the server falls back
          // to persisted report.state and may export a different doc set
          // than the user sees after applying unsaved filters.
          filterModel={gridFilterModelSelector(apiRef)}
          open={openDialog === 'bulk-export'}
          reportId={reportId}
          onClose={() => setOpenDialog(null)}
        />
      )}
      {typeof firstSelectedDocumentId === 'string' && (
        <DuplicateDocumentDialog
          documentId={firstSelectedDocumentId}
          handleClose={() => setOpenDialog(null)}
          open={openDialog === 'duplicate-document'}
          reportId={reportId}
        />
      )}
    </>
  );
}

export function useRequestReportExport() {
  const currentUser = useSelectCurrentUser();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const [sendReportExport] = Apollo.useSendReportExportMutation();
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    message: `A copy of the report has been sent to ${currentUser?.email.toLowerCase() ?? 'your email'}.`,
  });
  const requestReportExport = useCallback(
    async ({ format, reportId }: { format: Apollo.ExportFormat; reportId: UUID }) => {
      const loadSnackKey = uuid4();
      enqueueSnackbar('', {
        content: <LoadingSnackbar id={loadSnackKey} message="Preparing report for export..." />,
        key: loadSnackKey,
        persist: true,
      });
      const { data } = await sendReportExport({
        variables: { format, reportId },
      });
      closeSnackbar(loadSnackKey);
      if (data) dispatchSuccessSnack();
    },
    [enqueueSnackbar, sendReportExport, closeSnackbar, dispatchSuccessSnack]
  );

  return { requestReportExport };
}

function useFavoriteRows(args: { apiRef: React.RefObject<GridApiPremium | null> }) {
  const { apiRef } = args;
  const [favoriteStorables, { loading: submittingFavorites }] =
    Apollo.useFavoriteStorablesMutation();
  const { selectedDocumentIds } = useSelectedDocumentIds(apiRef);
  const handleFavoriteRows = useCallback(
    async (favorited: boolean): Promise<void> => {
      const { data } = await favoriteStorables({
        variables: {
          favoriteProps: {
            favorited,
            storableIds: selectedDocumentIds,
          },
        },
      });
      if (data?.favoriteStorables) {
        apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' });
      }
    },
    [apiRef, favoriteStorables, selectedDocumentIds]
  );

  return { handleFavoriteRows, submittingFavorites };
}

function useRenameDocumentRow() {
  const apiRef = useGridApiContext();
  const [updateDocument, { loading: renamingDocument }] = Apollo.useUpdateDocumentPropsMutation();
  const handleRenameDocument = useCallback(
    async (selectedDocumentId: UUID, name: string): Promise<void> => {
      const { data } = await updateDocument({
        variables: { input: { documentId: selectedDocumentId, documentProps: { name } } },
      });
      if (data) apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' });
    },
    [apiRef, updateDocument]
  );

  return { handleRenameDocument, renamingDocument };
}

const useFloatingActionButtonStyles = makeStyles()(theme => ({
  button: {
    minWidth: 'unset',
    padding: theme.spacing(0.75, 1),
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  wrapper: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderRadius: 100,
    bottom: 16,
    display: 'flex',
    gap: theme.spacing(1),
    left: '50%',
    padding: theme.spacing(1),
    position: 'absolute',
    transform: 'translateX(-50%)',
    width: 'fit-content',
    zIndex: theme.zIndex.appBar,
  },
}));

export function FloatingActionButton({
  isRelationshipGrouping = false,
  missingMatchesOnly = false,
  onDocumentsTrashed,
  onGroupTrashed,
  reportId,
  showRowsLockAction = true,
  totalDocumentCount,
  updateCacheOnTrash,
}: RepositoryActionsProps) {
  const apiRef = useGridApiContext();
  const { classes } = useFloatingActionButtonStyles();
  const dispatch = useNotificationDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { trashDocuments } = useTrashDocuments({ updateCacheOnTrash });
  const { selectedDocumentIds, selectedRowIds } = useSelectedDocumentIds(apiRef);
  const { openDrawer } = useGlobalDrawer();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Bumped after add/remove operations to trigger a re-render so the button label updates.
  const [, bumpChatVersion] = useState(0);
  const [trashDialogOpen, setTrashDialogOpen] = useState<boolean>(false);
  const [confirmAllDialogOpen, setConfirmAllDialogOpen] = useState<boolean>(false);
  const [bulkExportDialogOpen, setBulkExportDialogOpen] = useState<boolean>(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockJobIds, setLockJobIds] = useState<string[]>([]);
  const [lockAction, setLockAction] = useState<LockAction>('lock');
  const [toggleExtractionJobsLock] = Apollo.useToggleExtractionJobsLockMutation();
  const { exportToPdf, loading: exportingDocuments } = useExportDocumentsToPdf(selectedDocumentIds);
  const { handleFavoriteRows, submittingFavorites } = useFavoriteRows({
    apiRef,
  });
  const [bulkConfirm, { loading: confirmingRelationship }] =
    Apollo.useBulkConfirmDocumentRelationshipsMutation();
  const [bulkReject, { loading: rejectingRelationship }] =
    Apollo.useBulkRejectDocumentRelationshipsMutation();
  const [bulkReviewConfirmAction, setBulkReviewConfirmAction] = useState<
    'accept' | 'reject' | null
  >(null);
  const { shouldRunForAllDocuments } = useAreAllRowsSelected(apiRef, totalDocumentCount);
  /**
   * Per-group select-all: non-null when exactly one group is fully selected.
   * Recomputed each render; the component re-renders on selection change.
   */
  const selectedGroup = getSelectedGroup(apiRef);
  const isSelectAllRelationships = isRelationshipGrouping && shouldRunForAllDocuments;

  async function handleTrashDocuments(): Promise<void> {
    const filterModel = gridFilterModelSelector(apiRef);
    const runAllDocs = Boolean(selectedGroup) || shouldRunForAllDocuments;
    const { data } = await trashDocuments({
      documentIds: runAllDocs ? [] : selectedDocumentIds,
      filterModel,
      groupSelection: selectedGroup?.groupSelection ?? null,
      reportId,
      runAllDocs,
    });
    if (!data) return;

    const { documentIds: trashedIds, numSkipped, skippedDocumentIds } = data.disableDocuments;
    const trashedCount = selectedGroup
      ? selectedGroup.docCount - numSkipped
      : shouldRunForAllDocuments
        ? (typeof totalDocumentCount === 'number' ? totalDocumentCount : 0) - numSkipped
        : trashedIds.length;
    const successMsg = `Moved ${trashedCount} document${trashedCount === 1 ? '' : 's'} to trash.`;
    const skippedMsg =
      numSkipped > 0
        ? ` Skipped ${numSkipped} document${numSkipped === 1 ? '' : 's'} because those files are part of an active Workflow or you are not authorized.`
        : '';

    dispatch({
      payload: {
        autoHideDuration: 15000,
        key: uuid4(),
        message: successMsg + skippedMsg,
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });

    if (selectedGroup) {
      const groupDepth = apiRef.current?.getRowNode(selectedGroup.groupRowId)?.depth;
      /**
       * A fully-emptied root group (every member trashed, none skipped) is
       * dropped surgically by its row id — cache-independent, so it can't race
       * the lagging document-cache. Restricted to depth-0 groups: a nested
       * subgroup would also need its ancestor headers decremented, which the
       * surgical helper doesn't yet do. Anything else (partial skip, nested
       * subgroup) falls back to a no-cache root refetch.
       */
      if (numSkipped === 0 && groupDepth === 0 && onGroupTrashed) {
        onGroupTrashed(selectedGroup.groupRowId, trashedCount);
      } else {
        refreshDataGrid(apiRef);
      }
    } else {
      onDocumentsTrashed?.(
        computeTrashedDocIds({
          apiRef,
          runAll: shouldRunForAllDocuments,
          skippedDocumentIds,
          trashedIds,
        }),
        data.disableDocuments.affectedRelationshipParents
      );
    }
  }

  async function handleBulkExport(): Promise<void> {
    // "Select all" is only honored when we have a reportId — without it the
    // server has no way to know which documents the user means. Refuse here
    // rather than opening the dialog with both documentIds and reportId
    // unset (which would fail server-side after the user clicked Start).
    if (shouldRunForAllDocuments && !reportId) {
      dispatch({
        payload: {
          autoHideDuration: 6000,
          key: uuid4(),
          message: 'Export all is only available inside a report. Select documents to export.',
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }
    // In relationship grouping the "select all" checkbox covers only the
    // visible subtree, but the report reference doesn't carry that subtree
    // filter — sending {reportId} would silently export the entire report
    // (a superset of what the user sees). Refuse rather than over-export.
    if (isSelectAllRelationships) {
      dispatch({
        payload: {
          autoHideDuration: 6000,
          key: uuid4(),
          message:
            'Export all is not supported in relationship grouping. Select specific documents or exit grouping first.',
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }
    // Selections beyond the client-side cap, or "select all" against a report,
    // route to the server-side bulk-export pipeline.
    if (shouldRunForAllDocuments || selectedDocumentIds.length > maxFilesToDownload) {
      setBulkExportDialogOpen(true);
      return;
    }
    await exportToPdf();
  }

  /**
   * Subscribe to the chat input's chip-change counter so this bar re-renders
   * when chips are added or removed directly inside the input (e.g. clicking a
   * chip's 'x'). Without this, the toggle label would only refresh on selection
   * change or the bar's own add/remove actions, leaving "Remove from Chat"
   * stale after an in-input removal.
   */
  void useSnapshot($chatThreadState).documentChipVersion;

  /**
   * Read synchronously on every render — accurate whenever the component
   * re-renders (selection change, in-input chip change via documentChipVersion,
   * or after a mutation that bumps bumpChatVersion). Uses document chips only: a
   * selection mention shares a documentId but is not a chip, so it must not flip
   * the button to "Remove from Chat".
   */
  const currentChatDocumentIdSet = new Set($chatCallbacks.getDocumentChipIds?.() ?? []);

  const selectedMentionRows = useMemo(
    () => [...apiRef.current.getSelectedRows().values()].filter(getIsReportGridRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowIds]
  );

  const allSelectedInChat = getAllSelectedDocumentsInChat(
    selectedMentionRows,
    currentChatDocumentIdSet
  );

  /**
   * Chat mentions need each document's id AND name, which the grid only has for
   * loaded rows. A fully-selected group or repository-wide select-all can stand
   * for more documents than are materialized (the rest are resolved server-side
   * by other bulk actions via groupSelection / runAllDocs), so adding or removing
   * chips here would silently touch just the loaded subset. Detect that by
   * comparing the documents the selection represents against the loaded rows —
   * a small, fully-loaded select-all has no unloaded docs and stays supported.
   */
  const hasUnloadedSelectedDocuments = getHasUnloadedSelectedDocuments({
    loadedSelectedDocumentCount: new Set(selectedMentionRows.map(row => row.documentId)).size,
    selectedGroupDocCount: selectedGroup?.docCount ?? null,
    shouldRunForAllDocuments,
    totalDocumentCount,
  });

  /**
   * Make the chat input visible before mutating its chips. On a route-specific
   * drawer view (e.g. a document preview) GlobalRightDrawer hides the global AI
   * pane, so an insert/remove would hit an editor the user can't see. Navigate
   * back to the default AI route first (getBackToAIRoute returns null when
   * already on a global route), then open the drawer.
   */
  function revealChatDrawer(): void {
    const backToAiRoute = getBackToAIRoute(pathname);
    if (backToAiRoute) navigate(backToAiRoute, { replace: true });
    openDrawer();
  }

  function handleAddToChat(): void {
    if (hasUnloadedSelectedDocuments) {
      enqueueSnackbar(
        "Add to Chat isn't available when a whole group or all documents are selected. Select documents individually to add them.",
        { variant: 'info' }
      );
      return;
    }

    /**
     * Resolve the mentions to insert from the live chat state at click time
     * rather than the render-time `currentChatDocumentIdSet` snapshot. Chips
     * added or removed directly in the chat input don't re-render this bar, so
     * the snapshot can be stale; `buildDocumentMentionsToInsert` dedupes the
     * selection, folds in any mentions still queued from an earlier not-ready
     * insert (so that subset isn't stranded), and drops document ids already
     * present as chips (`insertDocumentMentions` doesn't dedupe).
     */
    const mentionsToInsert = buildDocumentMentionsToInsert({
      chatDocumentIdSet: new Set($chatCallbacks.getDocumentChipIds?.() ?? []),
      queuedMentions: $chatCallbacks.pendingDocumentMentions ?? [],
      selectedRows: selectedMentionRows,
    });

    /**
     * Reveal the chat drawer and bump the version unconditionally, even when
     * every selected document is already a chip. The render-time label can be
     * stale (chips changed directly in the chat input don't re-render this bar),
     * so a click that lands here with nothing new must still give feedback rather
     * than silently no-op: revealing the drawer surfaces the existing chips, and
     * the bump re-renders the bar so `allSelectedInChat` recomputes live and the
     * label flips to "Remove from Chat".
     */
    revealChatDrawer();
    if (mentionsToInsert.length > 0) {
      const inserted = $chatCallbacks.insertDocumentMentions?.(mentionsToInsert) ?? false;
      /**
       * On a confirmed insert, clear the queue entirely — every pending mention
       * was just inserted, so re-draining would create duplicate chips. On a
       * not-ready insert, queue the whole set so the registration effect drains
       * it once the editor mounts.
       */
      $chatCallbacks.pendingDocumentMentions = inserted ? null : mentionsToInsert;
    }
    bumpChatVersion(v => v + 1);
  }

  function handleRemoveFromChat(): void {
    if (hasUnloadedSelectedDocuments) {
      enqueueSnackbar(
        "Remove from Chat isn't available when a whole group or all documents are selected.",
        { variant: 'info' }
      );
      return;
    }

    const ids = selectedMentionRows.map(row => row.documentId);
    /**
     * Reveal the chat drawer first so the removal is visible: on a route-specific
     * drawer view the global AI pane is hidden, and removing chips from an editor
     * the user can't see would leave the visible UI unchanged.
     */
    revealChatDrawer();
    $chatCallbacks.removeDocumentMentions?.(ids);
    bumpChatVersion(v => v + 1);
  }

  const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const selectionModel = useGridSelector(apiRef, gridRowSelectionIdsSelector);

  /**
   * `selectionModel` and `rowsLookup` aren't read directly here — the
   * actual reads go through `apiRef.current.getSelectedRows()` — but
   * referencing them in the body re-runs this expression whenever the
   * selection or underlying row data changes, which is what triggers
   * the React Compiler to re-evaluate this on each render.
   */
  void selectionModel;
  void rowsLookup;
  const rowsLockInfo = getJobsLockState(
    collectRowQuestionJobs(apiRef.current.getSelectedRows().values())
  );

  const handleLockRows = (action: LockAction) => {
    /**
     * Snapshot the affected extractionJobIds at click time so the
     * confirmation modal's count and the mutation's payload remain
     * consistent even if the user changes selection while the modal
     * is open.
     */
    setLockJobIds(rowsLockInfo.extractionJobIds);
    setLockAction(action);
    setShowLockModal(true);
  };

  const handleLockConfirm = async () => {
    if (shouldRunForAllDocuments && !reportId) {
      enqueueSnackbar('Cannot lock all rows outside of a saved report.', { variant: 'error' });
      setShowLockModal(false);
      return;
    }
    try {
      await toggleExtractionJobsLock({
        /**
         * Row-level locks can affect any question; refetch every mounted
         * GetQuestionLockStatus so column headers reflect the new state.
         */
        refetchQueries: [Apollo.namedOperations.Query.GetQuestionLockStatus],
        variables: {
          input: {
            extractionJobIds: shouldRunForAllDocuments ? [] : lockJobIds,
            /**
             * Pass the live grid filter so 'Lock all rows' locks the
             * rows the user is actually looking at, not the saved
             * report.state filter. Same precedence the trash path uses.
             */
            filterModel: shouldRunForAllDocuments ? gridFilterModelSelector(apiRef) : null,
            isLocked: lockAction === 'lock',
            reportId,
            runAllJobs: shouldRunForAllDocuments,
          },
        },
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'report-rows-lock' } });
      enqueueSnackbar('Failed to update row lock. Please try again.', { variant: 'error' });
    } finally {
      setShowLockModal(false);
    }
  };

  const rowsLockAction = getLockAction(rowsLockInfo.state);

  const someDocNotFavorited = useMemo(() => {
    if (!selectionModel.size) return true;
    return [...selectionModel.keys()].some(id => {
      const row = rowsLookup[id];
      if (!row) return false;
      return !row.requesterFavorited;
    });
  }, [rowsLookup, selectionModel]);

  const documentCount = selectedGroup
    ? selectedGroup.docCount
    : shouldRunForAllDocuments
      ? totalDocumentCount
      : selectedDocumentIds.length;

  const bulkRelationshipTargets = useMemo(() => {
    if (!isRelationshipGrouping) return [];

    return selectedRowIds.flatMap(id => {
      const row = apiRef.current.getRow<ReportRow>(id);
      if (!row) return [];

      switch (row.__typename) {
        case 'Document': {
          if (!row.parentTreeRowId && row.childRowCount > 0) {
            return [{ id: row.documentId, type: Apollo.BulkRelationshipTargetType.Document }];
          }
          const relId = row.relationship?.id;
          return relId ? [{ id: relId, type: Apollo.BulkRelationshipTargetType.Relationship }] : [];
        }
        case 'UrlRelationshipRow':
          return [
            {
              id: row.urlRelationship.id,
              type: Apollo.BulkRelationshipTargetType.Relationship,
            },
          ];
        case 'OrphanRelationshipRow':
        case 'GroupHeaderRow':
        default:
          return [];
      }
    });
  }, [apiRef, isRelationshipGrouping, selectedRowIds]);

  const relationshipActionsDisabled =
    !bulkRelationshipTargets.length || confirmingRelationship || rejectingRelationship;
  const checkDisabled = relationshipActionsDisabled || missingMatchesOnly;

  async function executeBulkReview(action: 'accept' | 'reject'): Promise<void> {
    const mutate = action === 'accept' ? bulkConfirm : bulkReject;
    await mutate({
      variables: {
        input: {
          missingMatchesOnly,
          reportId: reportId ?? null,
          runAll: isSelectAllRelationships,
          targets: bulkRelationshipTargets,
        },
      },
    });
    apiRef.current.setRowSelectionModel({ ids: new Set(), type: 'include' });
  }

  function handleRejectSelectedRelationships(): void {
    if (!bulkRelationshipTargets.length) return;
    if (isSelectAllRelationships) {
      setBulkReviewConfirmAction('reject');
      return;
    }
    void executeBulkReview('reject');
  }

  function handleAcceptSelectedRelationships(): void {
    if (!bulkRelationshipTargets.length) return;
    if (isSelectAllRelationships) {
      setBulkReviewConfirmAction('accept');
      return;
    }
    void executeBulkReview('accept');
  }

  return (
    <>
      <Paper className={classes.wrapper} elevation={2} onClick={e => e.stopPropagation()}>
        <AlineButton
          loading={submittingFavorites}
          startIcon={
            <Star
              color={someDocNotFavorited ? IconColors.LightGray : IconColors.StarYellow}
              fill={someDocNotFavorited ? 'none' : IconColors.StarYellow}
              size={18}
            />
          }
          variant="outlined"
          onClick={async () => {
            await handleFavoriteRows(someDocNotFavorited ? true : false);
          }}
        >
          {someDocNotFavorited ? 'Favorite' : 'Unfavorite'}
        </AlineButton>
        <AlineButton
          loading={exportingDocuments}
          startIcon={<Download size={18} />}
          variant="outlined"
          onClick={handleBulkExport}
        >
          Download
        </AlineButton>
        <AlineButton
          startIcon={
            allSelectedInChat ? (
              <Minus size={18} />
            ) : (
              <CrossIcon style={{ height: 18, width: 18 }} />
            )
          }
          variant="outlined"
          onClick={allSelectedInChat ? handleRemoveFromChat : handleAddToChat}
        >
          {allSelectedInChat ? 'Remove from Chat' : 'Add to Chat'}
        </AlineButton>
        {showRowsLockAction && rowsLockAction && (
          <AlineTooltip arrow placement="top" title={ROWS_LOCK_LABEL[rowsLockAction]}>
            <span>
              <AlineButton
                buttonClassName={classes.button}
                variant="outlined"
                onClick={() => handleLockRows(rowsLockAction)}
              >
                <LockIcon action={rowsLockAction} iconProps={{ size: 18 }} />
              </AlineButton>
            </span>
          </AlineTooltip>
        )}
        {isRelationshipGrouping && (
          <>
            <AlineTooltip
              arrow
              placement="top"
              title={
                isSelectAllRelationships
                  ? 'Reject all relationships'
                  : bulkRelationshipTargets.length
                    ? `Reject ${bulkRelationshipTargets.length} selected ${bulkRelationshipTargets.length === 1 ? 'relationship' : 'relationships'}`
                    : 'No relationships selected'
              }
            >
              <span>
                <AlineButton
                  buttonClassName={classes.button}
                  disabled={relationshipActionsDisabled}
                  variant="outlined"
                  onClick={handleRejectSelectedRelationships}
                >
                  <CancelCircleIcon
                    fill={relationshipActionsDisabled ? IconColors.LightGray : undefined}
                  />
                </AlineButton>
              </span>
            </AlineTooltip>
            <AlineTooltip
              arrow
              placement="top"
              title={
                missingMatchesOnly
                  ? 'Cannot accept missing match relationships'
                  : isSelectAllRelationships
                    ? 'Accept all relationships'
                    : bulkRelationshipTargets.length
                      ? `Accept ${bulkRelationshipTargets.length} selected ${bulkRelationshipTargets.length === 1 ? 'relationship' : 'relationships'}`
                      : 'No relationships selected'
              }
            >
              <span>
                <AlineButton
                  buttonClassName={classes.button}
                  disabled={checkDisabled}
                  variant="outlined"
                  onClick={handleAcceptSelectedRelationships}
                >
                  <CheckCircleIcon fill={checkDisabled ? IconColors.LightGray : undefined} />
                </AlineButton>
              </span>
            </AlineTooltip>
          </>
        )}
        <AlineButton
          buttonClassName={classes.button}
          variant="outlined"
          onClick={() => setTrashDialogOpen(true)}
        >
          <Trash2 size={18} />
        </AlineButton>
        <RepositoryActions
          isRelationshipGrouping={isRelationshipGrouping}
          reportId={reportId}
          totalDocumentCount={totalDocumentCount}
        />
      </Paper>
      <ConfirmationDialog
        message={`Are you sure you want to move ${documentCount} document${documentCount === 1 ? '' : 's'} to trash?`}
        open={trashDialogOpen}
        primaryText="Move to trash"
        title="Move documents to trash"
        onClickPrimary={async () => {
          if (shouldRunForAllDocuments) {
            setConfirmAllDialogOpen(true);
            return;
          }
          await handleTrashDocuments();
        }}
        onClose={() => setTrashDialogOpen(false)}
      />
      <ConfirmationDialog
        message={createUpdateAllDocsMsg(
          'move to trash',
          typeof totalDocumentCount === 'number' ? totalDocumentCount : 0
        )}
        open={confirmAllDialogOpen}
        primaryText="Yes"
        secondaryText="No"
        title="Move all documents to trash?"
        onClickPrimary={handleTrashDocuments}
        onClose={() => setConfirmAllDialogOpen(false)}
      />
      {shouldRunForAllDocuments ? (
        <LockConfirmationModal
          action={lockAction}
          open={showLockModal}
          scope="all"
          target="rows"
          onCancel={() => setShowLockModal(false)}
          onConfirm={handleLockConfirm}
        />
      ) : (
        <LockConfirmationModal
          action={lockAction}
          count={lockJobIds.length}
          open={showLockModal}
          scope="selection"
          target="rows"
          onCancel={() => setShowLockModal(false)}
          onConfirm={handleLockConfirm}
        />
      )}
      <ConfirmationDialog
        message={
          bulkReviewConfirmAction === 'accept'
            ? 'This will accept all unconfirmed relationships across every document in this repository. This action cannot be undone.'
            : 'This will reject all unconfirmed relationships across every document in this repository. This action cannot be undone.'
        }
        open={bulkReviewConfirmAction !== null}
        primaryText={bulkReviewConfirmAction === 'accept' ? 'Accept All' : 'Reject All'}
        title={
          bulkReviewConfirmAction === 'accept'
            ? 'Accept all relationships?'
            : 'Reject all relationships?'
        }
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={async () => {
          if (bulkReviewConfirmAction) await executeBulkReview(bulkReviewConfirmAction);
        }}
        onClose={() => setBulkReviewConfirmAction(null)}
      />
      <BulkExportDialog
        documentCount={
          shouldRunForAllDocuments && typeof totalDocumentCount === 'number'
            ? totalDocumentCount
            : selectedDocumentIds.length
        }
        documentIds={shouldRunForAllDocuments ? undefined : selectedDocumentIds}
        // Only pass the live grid filterModel for the report select-all path —
        // it's a no-op for explicit documentIds exports.
        filterModel={shouldRunForAllDocuments ? gridFilterModelSelector(apiRef) : undefined}
        open={bulkExportDialogOpen}
        reportId={shouldRunForAllDocuments ? reportId : undefined}
        onClose={() => setBulkExportDialogOpen(false)}
      />
    </>
  );
}

/**
 * Resolves the document IDs that were effectively trashed so callers can
 * evict them from Apollo + the DataGrid. The server response is the
 * source of truth for `!runAll` flows, but for `runAll` the server
 * returns only skipped ids (to avoid serializing tens of thousands of
 * UUIDs over the wire), so we derive the effective set by subtracting
 * skipped ids from the doc ids currently loaded in the grid.
 */
function computeTrashedDocIds({
  apiRef,
  runAll,
  skippedDocumentIds,
  trashedIds,
}: {
  apiRef: React.RefObject<GridApiPremium | null>;
  runAll: boolean;
  skippedDocumentIds: readonly UUID[];
  trashedIds: readonly UUID[];
}): UUID[] {
  if (!runAll) return [...trashedIds];

  const skippedSet = new Set(skippedDocumentIds);
  const docIds = new Set<UUID>();
  if (!apiRef.current) return [];
  for (const row of apiRef.current.getRowModels().values()) {
    if (!getIsReportGridRow(row)) continue;
    if (skippedSet.has(row.documentId)) continue;
    docIds.add(row.documentId);
  }
  return [...docIds];
}
